import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff, Loader2, Clock, Check, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from './api';
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed } from '../../lib/pushNotifications';

export default function AcademyNotifySettings({ token, user }) {
  const [prefs, setPrefs] = useState({ enabled: false, hour: 19, minute: 0 });
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pwaInstallable, setPwaInstallable] = useState(false);

  useEffect(() => {
    Promise.all([
      apiCall('/reminders', 'GET', null, token).catch(() => null),
      isPushSubscribed(),
    ]).then(([r, sub]) => {
      if (r) setPrefs(p => ({ ...p, ...r }));
      setSubscribed(sub);
      setLoading(false);
    });
    // Detect if PWA is installable (event fired earlier and stored window flag)
    const handler = () => setPwaInstallable(true);
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [token]);

  const enable = async () => {
    setBusy(true);
    try {
      const ok = await subscribeToPush('academy_student', user?.email);
      if (!ok) {
        toast.error("Permission refusée. Autorise les notifications dans ton navigateur.");
        setBusy(false);
        return;
      }
      const tzOff = -new Date().getTimezoneOffset(); // minutes east of UTC
      const next = { ...prefs, enabled: true, timezone_offset: tzOff };
      await apiCall('/reminders', 'PUT', next, token);
      setPrefs(next);
      setSubscribed(true);
      toast.success("Rappels activés ! 🔔");
    } catch (e) { toast.error(e.message || "Erreur"); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      const next = { ...prefs, enabled: false };
      await apiCall('/reminders', 'PUT', next, token);
      setPrefs(next);
      setSubscribed(false);
      toast.success("Rappels désactivés.");
    } catch (e) { toast.error(e.message || "Erreur"); }
    setBusy(false);
  };

  const saveTime = async () => {
    setBusy(true);
    try {
      const tzOff = -new Date().getTimezoneOffset();
      await apiCall('/reminders', 'PUT', { ...prefs, timezone_offset: tzOff }, token);
      toast.success(`Rappel quotidien réglé sur ${String(prefs.hour).padStart(2, '0')}h${String(prefs.minute).padStart(2, '0')}`);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (loading) return <div className="text-white/40 text-xs py-3">Chargement des préférences...</div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="bg-gradient-to-br from-emerald-500/10 to-yellow-500/5 border border-emerald-400/20 rounded-2xl p-4"
      data-testid="academy-notify-settings">
      <div className="flex items-center gap-2 mb-3">
        {subscribed && prefs.enabled ? <Bell size={16} className="text-emerald-300" /> : <BellOff size={16} className="text-white/40" />}
        <h3 className="text-sm font-bold">Rappels quotidiens</h3>
      </div>

      {!subscribed || !prefs.enabled ? (
        <>
          <p className="text-xs text-white/60 mb-3">
            Active les notifications pour recevoir un petit rappel chaque jour à l'heure que tu choisis.
            Garder ta série est <span className="text-emerald-300 font-bold">3× plus efficace</span> que les sessions ponctuelles.
          </p>
          <button onClick={enable} disabled={busy}
            className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 font-bold py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
            data-testid="enable-reminders-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            Activer les rappels
          </button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 text-xs text-emerald-300">
            <Check size={12} />Rappels activés — heure de notification ci-dessous :
          </div>
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} className="text-white/40" />
            <select value={prefs.hour} onChange={e => setPrefs(p => ({ ...p, hour: Number(e.target.value) }))}
              data-testid="hour-select"
              className="bg-slate-900 border border-white/10 rounded-lg p-1.5 text-sm">
              {[...Array(24)].map((_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}h</option>)}
            </select>
            <span className="text-white/30">:</span>
            <select value={prefs.minute} onChange={e => setPrefs(p => ({ ...p, minute: Number(e.target.value) }))}
              data-testid="minute-select"
              className="bg-slate-900 border border-white/10 rounded-lg p-1.5 text-sm">
              {[0, 15, 30, 45].map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
            </select>
            <button onClick={saveTime} disabled={busy} data-testid="save-time-btn"
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-lg text-xs font-bold">
              Enregistrer
            </button>
          </div>
          <button onClick={disable} disabled={busy} data-testid="disable-reminders-btn"
            className="text-xs text-white/40 hover:text-red-300 flex items-center gap-1">
            <BellOff size={12} />Désactiver les rappels
          </button>
        </>
      )}

      {pwaInstallable && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-yellow-200">
          <Smartphone size={12} />Tu peux aussi <button onClick={() => window.dispatchEvent(new Event('show-pwa-prompt'))} className="underline">installer l'app</button> pour pratiquer hors-ligne.
        </div>
      )}
    </motion.div>
  );
}
